/**
 * WI-B-01 — the client session seam that turns a verified WorkOS token into
 * the shell's ProjectContext value. The ONE place a token becomes app state.
 *
 * Derives-from: architecture §1 Plane 1 (AuthKit token → {org_id, role});
 * B build-plan PIN-B-2 (two role axes) + attest 4aa7d81a80f8.
 *
 * TWO ROLE AXES, reconciled here so they can never diverge:
 *   - AUTHZ role  = current_user.role : WorkosRole (member|supervisor|admin,
 *     architecture §3). Server-verified via verifyAndMapWorkosToken; drives
 *     project visibility (workos-visibility.ts). CANONICAL.
 *   - UI-TIER role = config.role : member|supervisor|tenant_admin (bound to
 *     the coordinator Actor.role). Drives AdvancedModeGate ONLY.
 * `config.role` is DERIVED from the authz role by `deriveConfigRole` below — a
 * total, one-way map. Both fields come from the SAME verified token, so a
 * session can never carry an authz role that disagrees with its UI tier.
 *
 * This keeps workos-visibility.ts AND advanced-gate.tsx byte-identical
 * (B package AC-8: no consumer of ProvisionedClientConfig changed shape).
 */

import { verifyAndMapWorkosToken, WORKOS_HUMAN_OIDC_SOURCE, type WorkosCurrentUser } from "./workos-identity";
import type { WorkosRole } from "./workos-mock";
import type { ProvisionedClientConfig } from "../role/advanced-gate";

/** The UI-tier vocabulary mirrors the coordinator Actor role exactly. */
export type UiTierRole = ProvisionedClientConfig["role"];

/**
 * Map the canonical authz role onto the UI role. TOTAL and one-way; `admin` is
 * the only tenant-wide advanced tier while supervisor remains roster-scoped.
 *
 * A `supervisor` sees more PROJECTS than a member (workos-visibility.ts) but is
 * NOT an advanced-UI tier — the Advanced tab (master-list power filters, Reports
 * breadth) is admin-only. Conflating the two is exactly the drift PIN-B-2 exists
 * to prevent, which is why the map is explicit rather than a string cast.
 */
export function deriveConfigRole(role: WorkosRole): UiTierRole {
  switch (role) {
    case "admin":
      return "tenant_admin";
    case "supervisor":
      return "supervisor";
    case "member":
      return "member";
  }
}

/** The provisioned scope a real session bootstrap supplies alongside identity:
 * the tenant isolation key + the visible project slugs + the device bearer.
 * In the fixture leg this is seeded; the live leg (§S8) fetches it from the
 * session-bootstrap endpoint. `role` is NOT part of this input — it is derived
 * from the verified token, never supplied independently. */
export interface SessionScope {
  tenant_id: string;
  project_ids: string[];
  bearer: string;
}

/** Build the shell's ProvisionedClientConfig from the verified identity + the
 * provisioned scope. `role` is derived, never taken from `scope` — the caller
 * cannot inject a UI tier that disagrees with the token. */
export function configFromSession(
  currentUser: WorkosCurrentUser,
  scope: SessionScope,
): ProvisionedClientConfig {
  return {
    tenant_id: scope.tenant_id,
    project_ids: scope.project_ids,
    role: deriveConfigRole(currentUser.role),
    bearer: scope.bearer,
  };
}

/** The established-session shape the /callback (WI-B-02) seeds ProjectContext
 * with: the verified current user + the derived client config. */
export interface EstablishedSession {
  current_user: WorkosCurrentUser;
  config: ProvisionedClientConfig;
}

/** Durable local-queue owner key. Both components come from the authenticated
 * session authority; project membership is deliberately not part of identity. */
export function sessionOwnerPrincipal(session: EstablishedSession): string {
  const user = session.current_user.user_id.trim();
  const org = session.current_user.org_id.trim();
  if (!user || !org) throw new Error("session has no stable authenticated principal");
  return `${org}:${user}`;
}

/** A membership predicate: does `user_id` hold a membership in `org_id`, and if
 * so with what role? Returns null when the user is NOT a member of that org.
 * Bound to the WorkOS directory (MockWorkos.membershipOf in the fixture leg; the
 * live session-bootstrap endpoint in §S8). establishSession uses it to bind the
 * verified token's org to a REAL membership — a signature-valid token claiming
 * an org the user isn't a member of is rejected (cross-org isolation,
 * TW-workos-02), never trusted on the claim alone. */
export type MembershipCheck = (user_id: string, org_id: string) => { role: WorkosRole } | null;

/**
 * The whole client-side session bootstrap: verify a WorkOS token, then map it
 * (with the provisioned scope) onto the shell's session shape. Throws — via
 * verifyAndMapWorkosToken — on any invalid/expired/tampered/cross-issuer token,
 * so a bad token can never establish a session. This is the pure core the
 * /callback route (WI-B-02) wraps around a code→token exchange.
 *
 * When `checkMembership` is supplied, the verified token's `(user_id, org_id)`
 * is bound against the directory: a token whose org the user has no membership
 * in is REJECTED (cross-org isolation), and a token whose role claim disagrees
 * with the stored membership role is REJECTED (no role escalation via a stale
 * or forged claim). The token claim is never trusted as the sole authority for
 * org or role — the directory membership is.
 */
export function establishSession(
  token: string,
  scope: SessionScope,
  checkMembership?: MembershipCheck,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): EstablishedSession {
  const current_user = verifyAndMapWorkosToken(token, WORKOS_HUMAN_OIDC_SOURCE, nowSeconds);
  if (checkMembership) {
    const membership = checkMembership(current_user.user_id, current_user.org_id);
    if (!membership) {
      throw new Error("no membership for token org — cross-org token rejected");
    }
    if (membership.role !== current_user.role) {
      throw new Error("token role disagrees with directory membership — rejected");
    }
  }
  return { current_user, config: configFromSession(current_user, scope) };
}
